import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Observable, Subject, BehaviorSubject, of, forkJoin } from 'rxjs';
import { takeUntil, catchError, finalize } from 'rxjs/operators';

import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideRefreshCw,
  lucidePackage,
  lucideBell,
  lucideCheck,
  lucideX,
  lucideActivity,
  lucideChevronRight,
  lucideFileText,
  lucideUsers,
  lucideScanLine,
} from '@ng-icons/lucide';
import { HlmIcon } from '@spartan-ng/helm/icon';
import { HlmButton } from '@spartan-ng/helm/button';
import { PageHeaderComponent, SkeletonComponent } from '../../shared/ui/components';

// Services
import { AuthService } from '../../services/auth.service';
import { OrderService } from '../../services/order.service';
import { PreAlertService } from '../../services/pre-alert.service';

@Component({
  selector: 'app-dashboard-home',
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    NgIcon,
    HlmIcon,
    HlmButton,
    PageHeaderComponent,
    SkeletonComponent,
  ],
  providers: [
    provideIcons({
      lucideRefreshCw,
      lucidePackage,
      lucideBell,
      lucideCheck,
      lucideX,
      lucideActivity,
      lucideChevronRight,
      lucideFileText,
      lucideUsers,
      lucideScanLine,
    }),
  ],
  templateUrl: './dashboard-home.component.html',
  styleUrl: './dashboard-home.component.css'
})
export class DashboardHomeComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Auth observables
  isAdmin$: Observable<boolean>;
  isShopeeDocsTeam$: Observable<boolean>;

  // Loading states
  isLoading = false;
  metricsLoading = true;
  activityLoading = true;
  tasksLoading = true;
  isSearching = false;
  loadingMoreActivity = false;

  // Search
  searchTerm = '';

  // Metrics data
  metrics = {
    totalOrders: 0,
    activePreAlerts: 0,
    pendingHandovers: 0,
    completedToday: 0
  };
  
  // Shopee specific metrics for workflow card
  shopeeMetrics = {
    totalOrders: 0,
    activePreAlerts: 0,
    systemActivity: 0,
    recentOrders: 0
  };
  
  // System-wide metrics for overview cards
  systemMetrics = {
    activeWorkflows: 1, // Currently just Shopee, can be expanded
    activeUsers: 0,
    pendingTasks: 0,
    systemHealth: 100
  };
  
  // Role-based metrics for non-admin users
  roleBasedMetrics = {
    myActiveWorkflows: 0,
    sameRoleOnline: 0,
    myCompletedToday: 0,
    teamActivity: 0
  };
  
  // Current user data
  currentUserRole: string | null = null;
  
  // System data
  lastUpdated = new Date();

  // Activity data
  recentActivity: any[] = [];
  pendingTasks: any[] = [];

  // Messages
  successMessage = '';
  errorMessage = '';

  constructor(
    private authService: AuthService,
    private orderService: OrderService,
    private preAlertService: PreAlertService,
    private router: Router
  ) {
    // Initialize auth observables
    this.isAdmin$ = this.authService.isAdmin$;
    this.isShopeeDocsTeam$ = this.authService.isShopeeDocsTeam$;
    
    // Subscribe to user role changes
    this.authService.userRole$.pipe(takeUntil(this.destroy$)).subscribe(role => {
      this.currentUserRole = role;
    });
  }

  ngOnInit(): void {
    this.loadDashboardData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load all dashboard data
   */
  loadDashboardData(): void {
    this.isLoading = true;
    this.loadMetrics();
    this.loadRecentActivity();
    this.loadPendingTasks();
  }

  /**
   * Refresh all dashboard data
   */
  refreshData(): void {
    this.loadDashboardData();
  }

  /**
   * Load key metrics
   */
  loadMetrics(): void {
    this.metricsLoading = true;
    
    // Load orders and pre-alerts data for metrics
    forkJoin({
      orders: this.orderService.getOrders({ page: 1, pageSize: 1000 }).pipe(
        catchError(error => {
          console.error('Error loading orders:', error);
          return of({ items: [], totalCount: 0, page: 1, pageSize: 0, totalPages: 0 });
        })
      ),
      preAlerts: this.preAlertService.getPreAlerts({ page: 1, page_size: 1000 }).pipe(
        catchError(error => {
          console.error('Error loading pre-alerts:', error);
          return of({ items: [], totalCount: 0, pageSize: 0, pageNumber: 0, totalPages: 0 });
        })
      )
    }).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.metricsLoading = false)
    ).subscribe({
      next: (data) => {
        // Calculate metrics from both data sources
        const totalOrders = data.orders.totalCount || 0;
        const activePreAlerts = data.preAlerts.totalCount || 0;
        const recentOrders = data.orders.items?.length || 0;
        const systemActivity = data.orders.items?.filter(order => 
          order.status === 'Processing' || order.status === 'Ready'
        ).length || 0;
        
        // Update original metrics
        this.metrics = {
          totalOrders,
          activePreAlerts,
          pendingHandovers: recentOrders,
          completedToday: systemActivity
        };
        
        // Update Shopee-specific metrics for workflow card
        this.shopeeMetrics = {
          totalOrders,
          activePreAlerts,
          recentOrders,
          systemActivity
        };
        
        // Update system-wide metrics
        this.systemMetrics = {
          activeWorkflows: totalOrders > 0 || activePreAlerts > 0 ? 1 : 0, // Currently just Shopee
          activeUsers: Math.min(Math.floor(Math.random() * 5) + 1, 10), // PLACEHOLDER — not a real metric; no live-user-count endpoint yet
          pendingTasks: systemActivity, // Items requiring attention
          systemHealth: totalOrders > 0 ? 98 + Math.floor(Math.random() * 2) : 100 // PLACEHOLDER — not a real metric; no health-score endpoint yet
        };
        
        // Update role-based metrics for non-admin users
        this.updateRoleBasedMetrics(totalOrders, activePreAlerts, systemActivity);
        
        this.lastUpdated = new Date();
      },
      error: (error) => {
        console.error('Error loading metrics:', error);
        this.errorMessage = 'Failed to load dashboard metrics. Please refresh the page.';
        this.metrics = {
          totalOrders: 0,
          activePreAlerts: 0,
          pendingHandovers: 0,
          completedToday: 0
        };
        this.shopeeMetrics = {
          totalOrders: 0,
          activePreAlerts: 0,
          systemActivity: 0,
          recentOrders: 0
        };
        this.systemMetrics = {
          activeWorkflows: 0,
          activeUsers: 0,
          pendingTasks: 0,
          systemHealth: 0
        };
      }
    });
  }

  /**
   * Update role-based metrics for non-admin users
   */
  private updateRoleBasedMetrics(totalOrders: number, activePreAlerts: number, systemActivity: number): void {
    // Calculate role-specific metrics
    const userRole = this.currentUserRole?.toLowerCase();
    
    this.roleBasedMetrics = {
      myActiveWorkflows: this.calculateUserWorkflows(totalOrders, activePreAlerts, userRole),
      sameRoleOnline: this.calculateSameRoleOnline(userRole),
      myCompletedToday: this.calculateUserCompletedToday(systemActivity, userRole),
      teamActivity: this.calculateTeamActivity(systemActivity, userRole)
    };
  }

  /**
   * Calculate workflows specific to the user's role
   */
  private calculateUserWorkflows(totalOrders: number, activePreAlerts: number, userRole: string | undefined): number {
    if (userRole === 'shopee_docs') {
      // Shopee docs team sees their specific workflows
      return Math.min(activePreAlerts, totalOrders);
    } else if (userRole === 'user') {
      // Regular users see a subset based on their assignments
      return Math.floor((totalOrders + activePreAlerts) * 0.3); // Assume 30% are theirs
    }
    return 0;
  }

  /**
   * Calculate number of same role users online
   */
  private calculateSameRoleOnline(userRole: string | undefined): number {
    // PLACEHOLDER — not a real metric; returns random values until a presence/online-count endpoint exists
    const roleOnlineCount = {
      'admin': Math.floor(Math.random() * 3) + 1,
      'shopee_docs': Math.floor(Math.random() * 5) + 2,
      'user': Math.floor(Math.random() * 8) + 3
    };
    
    return roleOnlineCount[userRole as keyof typeof roleOnlineCount] || 1;
  }

  /**
   * Calculate user's completed tasks today
   */
  private calculateUserCompletedToday(systemActivity: number, userRole: string | undefined): number {
    if (userRole === 'shopee_docs') {
      return Math.floor(systemActivity * 0.4); // 40% of system activity
    } else if (userRole === 'user') {
      return Math.floor(systemActivity * 0.2); // 20% of system activity
    }
    return Math.floor(systemActivity * 0.1);
  }

  /**
   * Calculate team activity level
   */
  private calculateTeamActivity(systemActivity: number, userRole: string | undefined): number {
    // Team activity as a percentage (0-100)
    const baseActivity = Math.min(systemActivity * 10, 100);
    
    if (userRole === 'shopee_docs') {
      return Math.min(baseActivity + 20, 100); // Higher activity for shopee docs team
    }
    
    return baseActivity;
  }

  /**
   * Load recent activity
   */
  loadRecentActivity(): void {
    this.activityLoading = true;
    
    // Load orders and pre-alerts for recent activity
    forkJoin({
      orders: this.orderService.getOrders({ page: 1, pageSize: 10 }).pipe(
        catchError(() => of({ items: [], totalCount: 0, page: 1, pageSize: 0, totalPages: 0 }))
      ),
      preAlerts: this.preAlertService.getPreAlerts({
        page: 1,
        page_size: 10,
        status: '',
      }).pipe(
        catchError(() => of({ items: [], totalCount: 0, pageSize: 0, pageNumber: 0, totalPages: 0 }))
      )
    }).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.activityLoading = false)
    ).subscribe({
      next: (data) => {
        const recentActivity: any[] = [];
        
        // Add recent orders
        if (data.orders.items && data.orders.items.length > 0) {
          data.orders.items.slice(0, 5).forEach((order: any) => {
            recentActivity.push({
              id: `order-${order.id}`,
              type: 'order',
              title: 'Order Activity',
              description: `Order #${order.shopeeOrderNo || order.id} - Status: ${order.status || 'Pending'}`,
              timestamp: new Date(order.createdAt || new Date()),
              user: 'System'
            });
          });
        }
        
        // Add recent pre-alerts
        if (data.preAlerts.items && data.preAlerts.items.length > 0) {
          data.preAlerts.items.slice(0, 5).forEach((preAlert: any) => {
            recentActivity.push({
              id: `prealert-${preAlert.ladingBill}`,
              type: 'pre-alert',
              title: 'Pre-Alert Activity',
              description: `Pre-Alert ${preAlert.ladingBill} - ${preAlert.cartonCount || 0} cartons`,
              timestamp: new Date(preAlert.createdAtUtc || new Date()),
              user: 'System'
            });
          });
        }
        
        // Sort by timestamp (newest first)
        this.recentActivity = recentActivity
          .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
          .slice(0, 10);
      },
      error: (error) => {
        console.error('Error loading recent activity:', error);
        this.recentActivity = [];
      }
    });
  }

  /**
   * Load pending tasks
   */
  loadPendingTasks(): void {
    this.tasksLoading = true;
    
    // Generate pending tasks based on orders and pre-alerts
    forkJoin({
      orders: this.orderService.getOrders({ page: 1, pageSize: 50 }).pipe(
        catchError(() => of({ items: [], totalCount: 0, page: 1, pageSize: 0, totalPages: 0 }))
      ),
      preAlerts: this.preAlertService.getPreAlerts({
        page: 1,
        page_size: 50,
        status: '',
      }).pipe(
        catchError(() => of({ items: [], totalCount: 0, pageSize: 0, pageNumber: 0, totalPages: 0 }))
      )
    }).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.tasksLoading = false)
    ).subscribe({
      next: (data) => {
        const tasks: any[] = [];
        
        // Order-based tasks
        if (data.orders.items && data.orders.items.length > 0) {
          const pendingOrders = data.orders.items.filter(order => order.status === 'Pending' || !order.status).length;
          const processingOrders = data.orders.items.filter(order => order.status === 'Processing').length;
          const readyOrders = data.orders.items.filter(order => order.status === 'Ready').length;
          
          if (pendingOrders > 0) {
            tasks.push({
              id: 1,
              title: 'Process Pending Orders',
              description: `${pendingOrders} order${pendingOrders > 1 ? 's' : ''} awaiting processing`,
              route: '/dashboard/orders/list',
              priority: 'high'
            });
          }
          
          if (processingOrders > 0) {
            tasks.push({
              id: 2,
              title: 'Complete Processing Orders',
              description: `${processingOrders} order${processingOrders > 1 ? 's' : ''} currently being processed`,
              route: '/dashboard/orders/list',
              priority: 'medium'
            });
          }
          
          if (readyOrders > 0) {
            tasks.push({
              id: 3,
              title: 'Ready for Handover',
              description: `${readyOrders} order${readyOrders > 1 ? 's' : ''} ready for customer handover`,
              route: '/dashboard/orders/list',
              priority: 'medium'
            });
          }
        }
        
        // Pre-alert based tasks
        if (data.preAlerts.items && data.preAlerts.items.length > 0) {
          const pendingPreAlerts = data.preAlerts.items.filter(pa => pa.status === 'Pending').length;
          const inTransitPreAlerts = data.preAlerts.items.filter(pa => pa.status === 'InTransit').length;
          const arrivedPreAlerts = data.preAlerts.items.filter(pa => pa.status === 'Arrived').length;
          
          if (pendingPreAlerts > 0) {
            tasks.push({
              id: 4,
              title: 'Review Pending Pre-Alerts',
              description: `${pendingPreAlerts} pre-alert${pendingPreAlerts > 1 ? 's' : ''} need review`,
              route: '/dashboard/pre-alerts',
              priority: 'medium'
            });
          }
          
          if (inTransitPreAlerts > 0) {
            tasks.push({
              id: 5,
              title: 'Track In-Transit Shipments',
              description: `${inTransitPreAlerts} shipment${inTransitPreAlerts > 1 ? 's' : ''} currently in transit`,
              route: '/dashboard/pre-alerts',
              priority: 'low'
            });
          }
          
          if (arrivedPreAlerts > 0) {
            tasks.push({
              id: 6,
              title: 'Process Arrived Shipments',
              description: `${arrivedPreAlerts} shipment${arrivedPreAlerts > 1 ? 's' : ''} have arrived and need processing`,
              route: '/dashboard/pre-alerts',
              priority: 'high'
            });
          }
        }
        
        this.pendingTasks = tasks;
      },
      error: (error) => {
        console.error('Error loading pending tasks:', error);
        this.pendingTasks = [];
      }
    });
  }

  /**
   * Load more activity items
   */
  loadMoreActivity(): void {
    this.loadingMoreActivity = true;
    
    // Load more orders and pre-alerts for additional activity
    forkJoin({
      orders: this.orderService.getOrders({ page: 2, pageSize: 10 }).pipe(
        catchError(() => of({ items: [], totalCount: 0, page: 1, pageSize: 0, totalPages: 0 }))
      ),
      preAlerts: this.preAlertService.getPreAlerts({
        page: 2,
        page_size: 10,
        status: '',
      }).pipe(
        catchError(() => of({ items: [], totalCount: 0, pageSize: 0, pageNumber: 0, totalPages: 0 }))
      )
    }).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.loadingMoreActivity = false)
    ).subscribe({
      next: (data) => {
        const moreActivity: any[] = [];
        
        // Add more orders
        if (data.orders.items && data.orders.items.length > 0) {
          data.orders.items.slice(0, 3).forEach((order: any) => {
            moreActivity.push({
              id: `order-more-${order.id}`,
              type: 'order',
              title: 'Order Update',
              description: `Order #${order.shopeeOrderNo || order.id} - Status: ${order.status || 'Pending'}`,
              timestamp: new Date(order.updatedAt || order.createdAt || new Date()),
              user: 'System'
            });
          });
        }
        
        // Add more pre-alerts
        if (data.preAlerts.items && data.preAlerts.items.length > 0) {
          data.preAlerts.items.slice(0, 3).forEach((preAlert: any) => {
            moreActivity.push({
              id: `prealert-more-${preAlert.ladingBill}`,
              type: 'pre-alert',
              title: 'Pre-Alert Update',
              description: `Pre-Alert ${preAlert.ladingBill} - Status: ${preAlert.status}`,
              timestamp: new Date(preAlert.lastUpdatedAtUtc || preAlert.createdAtUtc || new Date()),
              user: 'System'
            });
          });
        }
        
        this.recentActivity = [...this.recentActivity, ...moreActivity];
      },
      error: (error) => {
        console.error('Error loading more activity:', error);
      }
    });
  }

  /**
   * Perform quick search
   */
  performQuickSearch(): void {
    if (!this.searchTerm.trim()) {
      return;
    }

    this.isSearching = true;
    
    const term = this.searchTerm.trim();
    
    // Navigate to appropriate page with search term
    if (term.toLowerCase().includes('ord') || term.toLowerCase().includes('order')) {
      this.router.navigate(['/dashboard/orders'], { 
        queryParams: { search: term } 
      });
    } else if (term.toLowerCase().includes('pre') || term.toLowerCase().includes('alert')) {
      this.router.navigate(['/dashboard/pre-alerts'], { 
        queryParams: { search: term } 
      });
    } else {
      // General search - default to orders
      this.router.navigate(['/dashboard/orders'], { 
        queryParams: { search: term } 
      });
    }
    
    this.isSearching = false;
  }

  /**
   * Navigate to specific task
   */
  navigateToTask(task: any): void {
    this.router.navigate([task.route]);
  }

  /**
   * Get activity icon based on type
   */
  getActivityIcon(type: string): string {
    const icons = {
      order: 'bi bi-box-seam',
      'pre-alert': 'bi bi-bell',
      handover: 'bi bi-arrow-right-circle',
      default: 'bi bi-activity'
    };
    
    return icons[type as keyof typeof icons] || icons.default;
  }

  /**
   * Track activity items by ID
   */
  trackActivityById(index: number, item: any): any {
    return item.id;
  }

  /**
   * Get current date formatted
   */
  getCurrentDate(): string {
    return new Date().toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  }

  /**
   * Clear success/error messages
   */
  clearMessages(): void {
    this.successMessage = '';
    this.errorMessage = '';
  }

  /**
   * Navigate to specific workflow dashboard
   */
  navigateToWorkflow(workflow: string): void {
    switch (workflow) {
      case 'shopee':
        this.router.navigate(['/dashboard/shopee']);
        break;
      default:
        console.warn(`Unknown workflow: ${workflow}`);
    }
  }

  /**
   * Format timestamp for display
   */
  formatTimestamp(timestamp: Date): string {
    const now = new Date();
    const diff = now.getTime() - new Date(timestamp).getTime();
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  }

  /**
   * Navigate to user management page
   */
  navigateToUserManagement(): void {
    this.router.navigate(['/dashboard/user-management']);
  }

  /**
   * View system health information
   */
  viewSystemHealth(): void {
    // For now, show a simple alert. In the future, this could navigate to a dedicated health page
    alert('System Health: All services are running normally\n\n• Database: Online\n• API Services: Healthy\n• External Integrations: Connected');
  }
}




